import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState, useCallback, useRef } from 'react';

export default function HeroSection() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const vantaRef = useRef(null);
  const vantaEffect = useRef(null);

  useEffect(() => { setMounted(true); }, []);

  // Lazy-load Three.js + Vanta HALO pour réduire le bundle initial (~600KB)
  useEffect(() => {
    if (!mounted || !vantaRef.current) return;

    let cancelled = false;

    async function initVanta() {
      const [{ default: HALO }, THREE] = await Promise.all([
        import('vanta/dist/vanta.halo.min'),
        import('three'),
      ]);

      if (cancelled || !vantaRef.current) return;

      // Détruire l'ancien effet si le thème change
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
        vantaEffect.current = null;
      }

      const isDark = resolvedTheme === 'dark';
      const isMobile = window.innerWidth < 768;

      vantaEffect.current = HALO({
        el: vantaRef.current,
        THREE,
        mouseControls: !isMobile,
        touchControls: true,
        gyroControls: false,
        minHeight: 100.00,
        minWidth: 100.00,
        baseColor: isDark ? 0x0c3d8a : 0x1a5bb5,
        backgroundColor: isDark ? 0x020612 : 0x050d1e,
        amplitudeFactor: isMobile ? 0.80 : 1.30,
        size: isMobile ? 1.75 : 2.20,
      });
    }

    initVanta();

    return () => {
      cancelled = true;
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
        vantaEffect.current = null;
      }
    };
  }, [mounted, resolvedTheme]);

  const scrollToElement = useCallback((elementId) => {
    const el = document.getElementById(elementId);
    el?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Vanta HALO Background */}
      <div ref={vantaRef} className="hero-bg" />
      {/* Overlay pour lisibilité du texte */}
      <div className="absolute inset-0 z-[1] bg-black/40 md:bg-black/25" />

      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl px-6 mx-auto">
        <div className="hero-text-animate hero-text-delay-1">
          <p className="text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] mb-6">
            Agence d'automatisation IA
          </p>
        </div>

        <h1 className={`hero-text-animate hero-text-delay-2 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-8 ${
          mounted && resolvedTheme === 'light' ? 'text-white' : 'text-foreground'
        }`}>
          Automatisez vos tâches répétitives avec{' '}
          <span className={`text-transparent bg-clip-text bg-gradient-to-b ${
            mounted && resolvedTheme === 'light' ? 'from-white to-white/70' : 'from-foreground to-foreground/60'
          }`}>
            l'intelligence artificielle
          </span>
        </h1>

        <p className={`hero-text-animate hero-text-delay-3 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed ${
          mounted && resolvedTheme === 'light' ? 'text-white/80' : 'text-muted-foreground'
        }`}>
          Nous automatisons les tâches répétitives de votre restaurant, cabinet médical ou commerce
          grâce à l'IA — en France et en Tunisie. Moins de travail manuel, plus de croissance.
        </p>

        <div className="hero-text-animate hero-text-delay-3 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            onClick={() => scrollToElement('audit')}
            data-testid="hero-cta-button"
            className="rounded-full px-8 h-12 text-base font-semibold bg-[hsl(211,100%,50%)] hover:bg-[hsl(211,100%,45%)] text-white btn-premium no-color-transition"
          >
            Réserver un Audit Gratuit
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            onClick={() => scrollToElement('services')}
            data-testid="hero-secondary-button"
            className={`rounded-full px-8 h-12 text-base font-medium hover:bg-secondary/80 ${
              mounted && resolvedTheme === 'light' ? 'text-white border-white/60' : 'border-border/60'
            }`}
          >
            Découvrir nos services
          </Button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hero-text-animate hero-text-delay-3 hidden md:flex">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <div className="w-1 h-2.5 rounded-full bg-muted-foreground/50 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
